import * as HttpStatusCodes from 'stoker/http-status-codes';
import { z, ZodSchema } from 'zod';
import { withRetry } from '../retry';

class PostHogError extends Error {
  public readonly isRetryable: boolean;

  constructor(message: string, isRetryable: boolean) {
    super(message);
    this.name = 'PostHogError';
    this.isRetryable = isRetryable;
  }

  static async fromResponse(response: Response, key: string): Promise<PostHogError> {
    let message: string;
    let isRetryable = true;

    switch (response.status) {
      case HttpStatusCodes.TOO_MANY_REQUESTS:
        message = 'Rate limited';
        break;
      case HttpStatusCodes.BAD_GATEWAY:
        message = 'Bad gateway';
        break;
      case HttpStatusCodes.SERVICE_UNAVAILABLE:
        message = 'Service unavailable';
        break;
      case HttpStatusCodes.GATEWAY_TIMEOUT:
      case 524: // Cloudflare timeout
        message = 'Timeout';
        break;
      default:
        message = `Error fetching remote config (status: ${response.status})`;
        isRetryable = false;
        break;
    }

    const responseText = await response.text();
    return new PostHogError(`[PostHog] ${message} for "${key}": ${responseText}`, isRetryable);
  }
}

// Schemas we use for remote config
export const baseRemoteConfigSchema = z.object({
  enabled: z.boolean(),
});
export type BaseRemoteConfig = z.infer<typeof baseRemoteConfigSchema>;

// Our generic remote config client
export interface PostHogEnv {
  POSTHOG_API_KEY: string;
  POSTHOG_HOST: string;
}

export interface PostHogClientOptions {
  host: string;
  projectId: string;
  featureFlagApiKey: string;
  apiKey: string;
}

export class PostHogClient {
  private readonly _host: string;
  private readonly _projectId: string;
  private readonly _featureFlagApiKey: string;
  private readonly _apiKey: string;
  public readonly env: PostHogEnv;

  constructor(options: PostHogClientOptions) {
    this._host = options.host;
    this._projectId = options.projectId;
    this._featureFlagApiKey = options.featureFlagApiKey;
    this._apiKey = options.apiKey;
    this.env = {
      POSTHOG_API_KEY: this._apiKey,
      POSTHOG_HOST: this._host,
    };
  }

  public async getRemoteConfigPayload<T>(key: string, schema: ZodSchema<T>): Promise<T | undefined> {
    return await withRetry(() => this._getRemoteConfigPayload(key, schema), {
      maxAttempts: 3,
      backoffMs: 1500, // PostHog has a 480/minute rate limit for read endpoints therefore, 1.5 seconds should be enough
      isRetryable: (e) => e instanceof PostHogError && e.isRetryable,
    });
  }

  public async isFeatureEnabled(key: string): Promise<boolean> {
    try {
      const payload = await this.getRemoteConfigPayload(key, baseRemoteConfigSchema);
      return payload?.enabled ?? false;
    } catch (error) {
      console.error(`Error checking if feature ${key} is enabled`, error);
      return false;
    }
  }

  private async _getRemoteConfigPayload<T>(key: string, schema: ZodSchema<T>): Promise<T | undefined> {
    const url = new URL(`api/projects/${this._projectId}/feature_flags/${key}/remote_config`, this._host);
    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${this._featureFlagApiKey}` },
    });

    if (response.status === HttpStatusCodes.NOT_FOUND) {
      return undefined;
    }

    if (!response.ok) {
      throw await PostHogError.fromResponse(response, key);
    }

    const text = (await response.json()) as string;
    const json = JSON.parse(text);
    return schema.parse(json);
  }
}

/**
 * Creates a PostHogClient from Cloudflare Worker environment variables.
 * This helper ensures type safety and centralizes the mapping logic.
 */
export function createPostHogClient(env: {
  POSTHOG_HOST: string;
  POSTHOG_PROJECT: string;
  POSTHOG_FEATURE_FLAG_API_KEY: string;
  POSTHOG_API_KEY: string;
}): PostHogClient {
  return new PostHogClient({
    host: env.POSTHOG_HOST,
    projectId: env.POSTHOG_PROJECT,
    featureFlagApiKey: env.POSTHOG_FEATURE_FLAG_API_KEY,
    apiKey: env.POSTHOG_API_KEY,
  });
}
